/**
 * What still stands between this campaign and being switched on.
 *
 * Each open item is a button that takes you to the tab that fixes it, because
 * "the sender address is missing" three tabs away from the sender field is a
 * complaint, not a step. When everything is closed the strip collapses to a
 * single line so a finished campaign does not carry a checklist forever.
 *
 * See readiness.ts for which items block activation and which are only advice.
 */
import { AlertTriangle, Check, CircleAlert } from "lucide-react";
import { useT } from "../../../../i18n/I18nProvider";
import { StatusPill } from "../../../ui";
import type { Check as ReadinessCheck, CheckTab, Readiness } from "./readiness";

/**
 * An open item, as a pressable status pill.
 *
 * A pill rather than the module's old hand-rolled bordered chip: the severity
 * is exactly what the CRM's `lt` and `wt` tones already mean, and an operator
 * who has learned that a red dot is a refusal on the invoice list should not
 * have to learn a second red here.
 */
function Item({
  check,
  label,
  onGo,
}: {
  check: ReadinessCheck;
  label: string;
  onGo: (tab: CheckTab) => void;
}) {
  const blocking = check.severity === "blocking";
  return (
    <button type="button" onClick={() => onGo(check.tab)} className="crm-ready-go">
      <StatusPill tone={blocking ? "lt" : "wt"}>
        {blocking ? <CircleAlert className="size-3.5 shrink-0" /> : <AlertTriangle className="size-3.5 shrink-0" />}
        {label}
      </StatusPill>
    </button>
  );
}

export function ReadinessStrip({ state, onGo }: { state: Readiness; onGo: (tab: CheckTab) => void }) {
  const t = useT();
  const open = state.checks.filter((c) => !c.ok);

  if (state.ready) {
    return (
      <div className="crm-card mb-3.5 flex items-center gap-2.5 p-3.5">
        <StatusPill tone="ok">
          <Check className="size-3.5" />
        </StatusPill>
        <p className="crm-sub min-w-0 flex-1">{t("marketing.ready.all")}</p>
      </div>
    );
  }

  // Split into two rows, because one sentence over a mixed row is how "2 things
  // are needed" ends up above four chips. Neither heading carries the number:
  // `t()` interpolates but does not pluralise, so a count in the string reads
  // "1 things are needed" as soon as one item is left, and the chips beside it
  // are already the count.
  const blocking = open.filter((c) => c.severity === "blocking");
  const advised = open.filter((c) => c.severity === "advised");

  return (
    <div className="crm-card mb-3.5 space-y-2.5 px-4 py-3.5">
      {blocking.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="text-[12.5px] font-[600]">{t("marketing.ready.blocking")}</span>
          <div className="flex flex-wrap items-center gap-1.5">
            {blocking.map((c) => (
              <Item key={c.id} check={c} label={t(`marketing.ready.${c.id}`)} onGo={onGo} />
            ))}
          </div>
        </div>
      ) : null}

      {advised.length > 0 ? (
        <div className="flex flex-wrap items-center gap-x-3 gap-y-2">
          <span className="crm-sub text-[12.5px] font-[600]">
            {t(blocking.length > 0 ? "marketing.ready.alsoAdvised" : "marketing.ready.advisedOnly")}
          </span>
          <div className="flex flex-wrap items-center gap-1.5">
            {advised.map((c) => (
              <Item key={c.id} check={c} label={t(`marketing.ready.${c.id}`)} onGo={onGo} />
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}
